import type { ProxyClientService } from './index';
import { AuthStatus, ProxyClient } from '@thinkalpha/table-client';
import type { ApiAuth, RawClient } from '@thinkalpha/table-client';
import type { Observable } from 'rxjs';
import { inject, injectable } from 'src/features/ioc';
import { appConfig } from 'src/lib/config';
import type { IsomorphicBindings } from 'src/types/bindings';

@injectable()
export class ProxyClientServiceImpl implements ProxyClientService {
    #client: ProxyClient;
    #disposableStack = new DisposableStack();
    #log: any;

    constructor(@inject('Logger') logger: IsomorphicBindings['Logger']) {
        // do not use superuser by default
        this.#client = new ProxyClient(appConfig.tableProxy, undefined, false, logger);
        this.#log = logger.getSubLogger({ name: 'table:authentication' });

        {
            const subscription = this.#client.authStatus$.subscribe((status) => {
                if (status === AuthStatus.badToken) {
                    this.#log.warn({ message: 'table-ws has rejected the user token' });
                }
            });
            this.#disposableStack.defer(subscription.unsubscribe);
        }

        {
            const subscription = this.#client.objectDefinitionResult$.subscribe((res) => {
                if (!res.success) {
                    this.#log.error({ message: res.comment });
                }
            });
            this.#disposableStack.defer(subscription.unsubscribe);
        }
    }

    get client(): ProxyClient {
        return this.#client;
    }

    get rawClient(): RawClient {
        return this.#client;
    }

    setAccessToken(accessToken: string | ApiAuth | undefined): void {
        if (TRACE) {
            this.#log.trace({ message: 'About to log in using token', accessToken });
        }
        this.#client.token = accessToken;
    }

    setSuperuserFlag(flag: boolean): void {
        if (TRACE) {
            this.#log.trace({ message: `About to set superuser token to ${flag}`, flag });
        }
        this.#client.useSuperuser = flag;
    }

    getAuthStatus$(): Observable<AuthStatus | undefined> {
        return this.#client.authStatus$;
    }

    // TODO: Commenting this is a temporary workaround for a bug in ReactiveInjectable which does not consider if an
    // injectable is "owned" (new instance) or "shared" (singleton instance).
    /*
[Symbol.dispose](): void {
this.#disposableStack.dispose();
}
*/
}
